import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { SocialProvider } from "@/lib/social";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import AgeGate from "@/components/AgeGate";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Browse from "@/pages/Browse";
import Channel from "@/pages/Channel";
import Tournaments from "@/pages/Tournaments";
import Plans from "@/pages/account/Plans";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Dashboard from "@/pages/Dashboard";
import DashboardShell from "@/pages/dashboard/DashboardShell";
import AlertOverlay from "@/pages/AlertOverlay";
import Games from "@/pages/Games";
import NeonSerpent from "@/pages/NeonSerpent";
import Leaderboard from "@/pages/Leaderboard";
import Credits from "@/pages/Credits";
import Messenger from "@/pages/Messenger";
import Admin from "@/pages/Admin";
import Account from "@/pages/Account";
import CommunityRedirect from "@/pages/CommunityRedirect";
import LFG from "@/pages/LFG";
import Shop from "@/pages/Shop";
import Profile from "@/pages/Profile";
import StreamerHome from "@/pages/StreamerHome";
import ChannelLeaderboards from "@/pages/ChannelLeaderboards";
import { PartiesList, PartyRoom } from "@/pages/Parties";
import Footer from "@/components/Footer";
import About from "@/pages/legal/About";
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import Community from "@/pages/legal/Community";
import Cookies from "@/pages/legal/Cookies";
import Safety from "@/pages/legal/Safety";
import DMCA from "@/pages/legal/DMCA";
import Refunds from "@/pages/legal/Refunds";
import Careers from "@/pages/legal/Careers";
import Contact from "@/pages/legal/Contact";
import FriendsDock from "@/components/FriendsDock";
import StreamingBubble from "@/components/StreamingBubble";
import StreamRecordingModal from "@/components/StreamRecordingModal";
import SupportBubble from "@/components/SupportBubble";
import TicketView from "@/pages/TicketView";
import AdminSupport from "@/pages/AdminSupport";
import AdminFinance from "@/pages/AdminFinance";
import AdminRbac from "@/pages/AdminRbac";
// Founder UI temporarily disabled. Route, page component, achievement
// watcher, and all display components remain on disk under
// /app/frontend/src/components/founders/ and /app/frontend/src/pages/
// FoundersHallOfFame.jsx. Backend endpoints under /api/founders/* and
// /api/admin/founders/* remain live. To restore, re-add the two imports
// below and their JSX (route + watcher).
import { StreamingProvider } from "@/lib/streaming";
import { LoadoutProvider } from "@/lib/loadouts";
import { api } from "@/lib/api";
import ComingSoon from "@/pages/ComingSoon";
import PreviewAccess from "@/pages/PreviewAccess";
import {
  isPrelaunch,
  isPathAllowedInPrelaunch,
} from "@/lib/prelaunch";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-center text-zinc-400">Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  return children;
}

function Shell() {
  const [ageOk, setAgeOk] = useState(() => !!localStorage.getItem("sc_age_ok"));
  const { user } = useAuth();
  // Re-evaluate the prelaunch state whenever auth state changes so a fresh
  // admin login can unlock the app without needing a hard refresh.
  const prelaunch = isPrelaunch();

  useEffect(() => {
    // Seed endpoint is now super-admin gated (SEC hardening) — no longer
    // auto-called on boot. Super-admins can trigger it manually from the
    // admin panel if they need to reset demo data.
  }, [prelaunch]);

  // If user is logged in we consider them age-verified
  useEffect(() => {
    if (user && !ageOk) {
      localStorage.setItem("sc_age_ok", "1");
      setAgeOk(true);
    }
  }, [user, ageOk]);

  // ------------------------------------------------------------------
  // OBS Browser Source — /alert-overlay must render standalone (no navbar,
  // no support bubble, no age gate) so streamers get a truly transparent
  // capture surface in OBS. Handle before both prelaunch + normal shells.
  // ------------------------------------------------------------------
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  if (pathname === "/alert-overlay") {
    return (
      <Routes>
        <Route path="/alert-overlay" element={<AlertOverlay />} />
      </Routes>
    );
  }

  // ------------------------------------------------------------------
  // PRELAUNCH MODE — public visitors only see ComingSoon + legal pages.
  // Signup/login/purchase flows are hidden. Backend hard-blocks the
  // sensitive endpoints too (defense in depth).
  // ------------------------------------------------------------------
  if (prelaunch) {
    // Force any non-whitelisted URL back to `/`.
    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    const isTicketView = path.startsWith("/support/ticket/");
    if (!isPathAllowedInPrelaunch(path) && !isTicketView) {
      return (
        <div className="App">
          <Routes>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      );
    }
    return (
      <div className="App">
        <main>
          <Routes>
            <Route path="/" element={<ComingSoon />} />
            <Route path="/preview" element={<PreviewAccess />} />
            <Route path="/about" element={<About />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/community" element={<Community />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/safety" element={<Safety />} />
            <Route path="/dmca" element={<DMCA />} />
            <Route path="/refunds" element={<Refunds />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/support/ticket/:id" element={<TicketView />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        <SupportBubble />
        <Toaster />
      </div>
    );
  }

  return (
    <div className="App">
      {!ageOk && <AgeGate onPass={() => setAgeOk(true)} />}
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/browse" element={<Browse />} />
          <Route path="/games" element={<Games />} />
          <Route path="/games/neon-serpent" element={<NeonSerpent />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/credits" element={<Credits />} />
          <Route path="/lfg" element={<LFG />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/tournaments" element={<Tournaments />} />
          <Route path="/account/plans" element={<Plans />} />
          {/* Legacy routes redirect to the new Plans page inside Account. */}
          <Route path="/subscribe" element={<Navigate replace to="/account/plans" />} />
          <Route path="/premium" element={<Navigate replace to="/account/plans" />} />
          <Route path="/my-subs" element={<Navigate replace to="/account/plans" />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/c/:username" element={<StreamerHome />} />
          <Route path="/c/:username/live" element={<Channel />} />
          {/* /c/:username/community route removed — community content is now
              inlined on the streamer's home page. Any legacy link redirects. */}
          <Route path="/c/:username/community" element={<CommunityRedirect />} />
          <Route path="/c/:username/leaderboards" element={<ChannelLeaderboards />} />
          <Route path="/u/:username" element={<Profile />} />
          <Route path="/dashboard/legacy" element={<Protected><Dashboard /></Protected>} />
          <Route path="/dashboard/*" element={<Protected><DashboardShell /></Protected>} />
          <Route path="/messages" element={<Protected><Messenger /></Protected>} />
          <Route path="/admin" element={<Protected><Admin /></Protected>} />
          <Route path="/admin/support" element={<Protected><AdminSupport /></Protected>} />
          <Route path="/admin/finance" element={<Protected><AdminFinance /></Protected>} />
          <Route path="/admin/rbac" element={<Protected><AdminRbac /></Protected>} />
          <Route path="/support/ticket/:id" element={<TicketView />} />
          <Route path="/account" element={<Protected><Account /></Protected>} />
          <Route path="/parties" element={<Protected><PartiesList /></Protected>} />
          <Route path="/parties/:partyId" element={<Protected><PartyRoom /></Protected>} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/community" element={<Community />} />
          <Route path="/cookies" element={<Cookies />} />
          <Route path="/safety" element={<Safety />} />
          <Route path="/dmca" element={<DMCA />} />
          <Route path="/refunds" element={<Refunds />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
      <Footer />
      <FriendsDock />
      <StreamingBubble />
      <StreamRecordingModal />
      <SupportBubble />
      <Toaster />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SocialProvider>
          <StreamingProvider>
            <LoadoutProvider>
              <Shell />
            </LoadoutProvider>
          </StreamingProvider>
        </SocialProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
